import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, shareReplay, tap, finalize } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AuthResponse,
  AuthUser,
  LoginPayload,
  RegisterPayload,
  Tokens,
} from '../models/api.models';
import { TokenStorage } from './token.storage';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly tokens = inject(TokenStorage);
  private readonly router = inject(Router);

  private readonly userSignal = signal<AuthUser | null>(null);
  private readonly accessToken = signal<string | null>(this.tokens.getAccessToken());

  readonly user = this.userSignal.asReadonly();
  readonly isLoggedIn = computed(() => !!this.accessToken());
  private refreshInFlight$: Observable<Tokens> | null = null;

  private readonly base = `${environment.apiUrl}/api/auth`;

  register(payload: RegisterPayload) {
    return this.http
      .post<AuthResponse>(`${this.base}/register`, payload, {
        withCredentials: true,
      })
      .pipe(tap((res) => this.applyAuth(res)));
  }

  login(payload: LoginPayload) {
    return this.http
      .post<AuthResponse>(`${this.base}/login`, payload, {
        withCredentials: true,
      })
      .pipe(tap((res) => this.applyAuth(res)));
  }

  logout() {
    return this.http
      .post<void>(
        `${this.base}/logout`,
        { refreshToken: this.tokens.getRefreshToken() },
        { withCredentials: true },
      )
      .pipe(
        tap({
          next: () => this.clearSession(),
          error: () => this.clearSession(),
        }),
      );
  }

  refresh(): Observable<Tokens> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    this.refreshInFlight$ = this.http
      .post<Tokens>(
        `${this.base}/refresh`,
        { refreshToken: this.tokens.getRefreshToken() },
        { withCredentials: true },
      )
      .pipe(
        tap((res) => {
          this.tokens.setTokens(res.accessToken, res.refreshToken);
          this.accessToken.set(res.accessToken);
        }),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay(1),
      );

    return this.refreshInFlight$;
  }

  sessionExpired(): void {
    this.clearSession();
  }

  private applyAuth(res: AuthResponse): void {
    this.tokens.setTokens(res.accessToken, res.refreshToken);
    this.accessToken.set(res.accessToken);
    this.userSignal.set(res.user);
  }

  private clearSession(): void {
    this.tokens.clear();
    this.accessToken.set(null);
    this.userSignal.set(null);
    // void this.router.navigateByUrl('/login');
    void this.router.navigateByUrl('/');
  }
}
